<template>
  <Dialog v-if="props.show && props.company_id" title="Create Customer" :sm="true" :show-close-button="true"
    :show-cancel-button="true" :show-save-button="true" :show-footer="true" save-button-text="Create Customer"
    :save-button-props="{ form: 'create-customer-form', type: 'submit' }"
    :saveButtonDisabled="is_creating_customer" @closeEvent="handleClose" @cancelEvent="handleClose">
    <template #modal-content>
      <Form id="create-customer-form" class="pt-4" @submit="handleCreateCustomer">
        <div class="flex items-start gap-3 p-3 mb-4 rounded border border-blue-100 bg-blue-50 text-sm text-blue-800"
          role="note" aria-label="General information">
          <svg class="w-5 h-5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path fill-rule="evenodd"
              d="M18 10A8 8 0 11 2 10a8 8 0 0116 0zM9 7a1 1 0 112 0 1 1 0 01-2 0zm1 3a1 1 0 00-1 1v3a1 1 0 102 0v-3a1 1 0 00-1-1z"
              clip-rule="evenodd" />
          </svg>
          <div>
            <!-- <div class="font-medium">General information</div> -->
            <div class="text-xs text-blue-700/90">Note: If email communication is disabled, no emails of any kind will
              be sent to this customer. This setting is permanent until manually re-enabled.</div>
            <!-- <div class="text-xs text-blue-700/90">Please enter the customer's first name and select the customer. Fields marked with <span class="text-red-500">*</span> are required.</div> -->
          </div>
        </div>
        <div class="flex flex-col w-full gap-2">
          <!-- General Information Section -->
          <div class="grid grid-cols-2 gap-4">
            <!-- Firstname -->
            <div>
              <label for="firstname" class="block mb-1 text-sm font-medium text-gray-700">First Name
                <span class="text-red-500">*</span>
              </label>
              <input type="text" id="firstname" v-model="customer.first_name" placeholder="Enter customer first name"
                class="input" />
              <span v-if="errorMessages['first_name']" class="text-xs text-red-500">{{
                errorMessages['first_name']
                }}</span>
            </div>

            <!-- Lastname -->
            <div>
              <label for="lastname" class="block mb-1 text-sm font-medium text-gray-700">Last Name<span
                  class="text-red-500">*</span>
              </label>
              <input id="lastname" type="text" v-model="customer.last_name" placeholder="Enter customer last name"
                class="input" />
              <span v-if="errorMessages['last_name']" class="text-xs text-red-500">{{
                errorMessages['last_name']
                }}</span>
            </div>

            <!-- Email -->
            <div>
              <label for="email" class="block mb-1 text-sm font-medium text-gray-700">Email
                <span class="text-red-500">*</span>
              </label>
              <input type="text" id="email" v-model="customer.email" placeholder="Enter customer email" class="input" />
              <span v-if="errorMessages['email']" class="text-xs text-red-500">{{
                errorMessages['email']
                }}</span>
            </div>
            <!-- Phone Number -->
            <div>
              <label for="phone_number" class="block mb-1 text-sm font-medium text-gray-700">Phone Number</label>
              <input id="phone_number" v-model="customer.phone_number" type="text"
                placeholder="Enter customer phone number" class="input" />
              <span v-if="errorMessages['phone_number']" class="text-xs text-red-500">{{
                errorMessages['phone_number']
                }}</span>
            </div>
            <!-- PO Document -->
            <div>
              <label class="inline-flex items-center">
                <Switch @update:modelValue="updateNotifications" :checked="is_notifications_enabled"
                  id="is_notifications_enabled" type="checkbox" title="" name="notifications_enabled" />
                <span class="ml-2">Enable Email Communication</span>
              </label>
            </div>
          </div>
        </div>
      </Form>
    </template>
  </Dialog>
</template>
<script lang="ts" setup>
import Dialog from '@/components/Shared/Dialog.vue';
import { Customer } from '@/models';
import { defineProps, reactive, ref } from 'vue';
import Switch from '@/components/Shared/Switch.vue';
import { http } from '@/interceptors/axiosRequestInterceptor';
import { showAlert } from '@/composables/swal';


const props = defineProps<{
  company_id: number | null;
  show: boolean;
}>();

const emits = defineEmits(['customerCreated', 'close']);

const errorMessages = reactive<Record<string, string>>({})
const customer = reactive<Customer>({
    id: 0,
    name: '',
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    company_name: '',
    addresses: [],
  })
const is_notifications_enabled = ref<boolean>(true);
const is_creating_customer = ref<boolean>(false);

const updateNotifications = (value: boolean) => {
  is_notifications_enabled.value = value;
};

const handleCreateCustomer = ($event) => {
  $event.preventDefault();
  // Handle create customer logic here
  const customerData = {
    first_name: customer.first_name,
    last_name: customer.last_name,
    email: customer.email,
    phone_number: customer.phone_number,
    is_notifications_enabled: is_notifications_enabled.value,
    manual_order: true,
    company_id: props.company_id, // Set this to the appropriate company ID if needed
  };
  is_creating_customer.value = true;
  http.post('/create/customer', customerData)
    .then((response) => {
      is_creating_customer.value = false;
      // Handle success (e.g., close modal, show success message)
      if (response.status === 200) {
        // Close modal or reset form as needed
        showAlert('Success', 'Customer created successfully!', 'success');
        customer.id = response.data.result.customer.id;
        customer.name = response.data.result.customer.name;
        emits('customerCreated', customer);
      }
      else {
        // Handle unexpected response
        console.error('Unexpected response:', response);
      }
    })
    .catch((error) => {
      is_creating_customer.value = false;
      // Handle error (e.g., display error messages)
      if (error.response && error.response.data && error.response.data.errors) {
        const errors = error.response.data.errors;
        for (const key in errors) {
          if (errors.hasOwnProperty(key)) {
            errorMessages[key] = errors[key][0];
          }
        }
        showAlert('Error', 'Please fix the errors in the form.', 'error');
      }
    });
};

const handleClose = () => {
  emits('close');
};
</script>
